import React, { Component, ErrorInfo } from 'react';
import { Link } from 'react-router-dom';

import { Button } from 'components/Button';

import * as S from './ErrorBoundaryInner.styles';

interface Props {
  children: React.ReactNode;
  hasError: boolean;
  setHasError: (hasError: boolean) => void;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Props, _previousState: State) {
    if (!this.props.hasError && prevProps.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Previously discarded silently (both params were unused) — a caught
    // render crash showed the "Something went wrong" screen with no trace
    // anywhere in devtools (no console error, no failed network request),
    // making it look like nothing had actually gone wrong.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.setHasError(true);
  }

  render() {
    if (this.state.hasError) {
      return (
        <S.Container>
          <S.Icon />
          <S.Title>Something went wrong</S.Title>
          <S.Description>
            There was a problem processing the request. Please, try again.
          </S.Description>
          <Link to="/" onClick={() => this.setState({ hasError: false })}>
            <Button
              type="button"
              scale="lg"
              textTransform="uppercase"
              fontFamily="LFT Zeno Condensed"
              motive="m4"
            >
              Back home
            </Button>
          </Link>
        </S.Container>
      );
    }

    return this.props.children;
  }
}

// eslint-disable-next-line import/no-default-export
export default ErrorBoundaryInner;
